/**
 * useVisibilityRefetch — B-40 BUG 4b.
 *
 * Refetch when the user returns to a tab/app that has been backgrounded for
 * a while. The founder saw a STALE Today (the Airo card + hero showed an old
 * week) that a manual refresh fixed — prod DB matched the phone, so it wasn't
 * a data bug, it was the absence of any focus/visibility refetch in the Today
 * data hooks (useWeeklyPlan / useTodaySession only refetch on their deps;
 * app-wide, only useNotifications had a visibility listener).
 *
 * Behaviour:
 *   - On `visibilitychange` to hidden → record when we went hidden.
 *   - On `visibilitychange` back to visible → if we were hidden for at least
 *     `minHiddenMs` (default 60s), call `refetch`. A brief tab-switch (under
 *     the threshold) does NOT refetch, so this never turns into a chatty
 *     refetch-on-every-blur.
 *
 * Operational (CAPACITY) — this only changes WHEN already-authorised reads
 * re-run; it does not touch strategy/scoring, so no validation-epoch impact.
 *
 * The callback is held in a ref (mirrors `useNotifications`' fetchRef) so a
 * fresh `refetch` closure each render never re-subscribes the listener.
 */
import { useEffect, useRef } from 'react';

/** Default: only refetch after the tab was hidden for a full minute. */
export const DEFAULT_MIN_HIDDEN_MS = 60_000;

export function useVisibilityRefetch(
  refetch: () => void,
  opts: { minHiddenMs?: number } = {},
): void {
  const minHiddenMs = opts.minHiddenMs ?? DEFAULT_MIN_HIDDEN_MS;

  // Keep the latest callback without re-subscribing the listener.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // When we last went hidden (null = currently/again visible or never hidden).
  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      // Back to visible.
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt != null && Date.now() - hiddenAt >= minHiddenMs) {
        refetchRef.current();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [minHiddenMs]);
}

export default useVisibilityRefetch;
